import React, { useState } from "react";
import {
    Box,
    Button,
    Dialog,
    FormControlLabel,
    IconButton,
    LinearProgress,
    Skeleton,
    Switch,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import CloudQueueRoundedIcon from "@mui/icons-material/CloudQueueRounded";

/* ------------------------------------------------------------------ */
/* Tokens                                                              */
/* ------------------------------------------------------------------ */
const C = {
    ink: "#0F2A3D",
    inkSoft: "#5B7083",
    files: "#12808E",
    filesBright: "#35C6D6", // files on dark background
    db: "#F0A23B",
    index: "#7C6CF0",
    free: "#E4EBF0",
    line: "#E7EDF2",
    mist: "#F5F8FA",
};

const fmtNum = (v) => Number(v ?? 0).toLocaleString("en-IN");
const fmtBytes = (b) =>
    b >= 1024 ** 3
        ? `${(b / 1024 ** 3).toFixed(2)} GB`
        : `${(b / 1024 ** 2).toFixed(1)} MB`;

/* ------------------------------------------------------------------ */
/* Building blocks                                                     */
/* ------------------------------------------------------------------ */
const Dot = ({ color, size = 9 }) => (
    <Box
        sx={{
            width: size,
            height: size,
            borderRadius: "50%",
            bgcolor: color,
            flexShrink: 0,
        }}
    />
);

/** Donut ring made from stroke segments */
const Ring = ({ size, stroke, segments, children }) => {
    const r = (size - stroke) / 2;
    const c = size / 2;
    const circ = 2 * Math.PI * r;
    const total = segments.reduce((a, s) => a + s.value, 0) || 1;
    const gap = 5;
    let offset = 0;

    return (
        <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                style={{ transform: "rotate(-90deg)", display: "block" }}
                role="img"
                aria-label="Storage split between files, database and free space"
            >
                <circle
                    cx={c}
                    cy={c}
                    r={r}
                    fill="none"
                    stroke="rgba(255,255,255,0.08)"
                    strokeWidth={stroke}
                />
                {segments.map((s) => {
                    const share = (s.value / total) * circ;
                    const len = Math.max(0, share - gap);
                    const el = (
                        <circle
                            key={s.key}
                            cx={c}
                            cy={c}
                            r={r}
                            fill="none"
                            stroke={s.color}
                            strokeWidth={stroke}
                            strokeLinecap="round"
                            strokeDasharray={`${len} ${circ - len}`}
                            strokeDashoffset={-offset}
                        />
                    );
                    offset += share;
                    return el;
                })}
            </svg>
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

const LegendRow = ({ color, label, value, loading }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            py: { xs: 0.35, md: 0.9 },
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Dot color={color} />
            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.78)" }}>
                {label}
            </Typography>
        </Box>
        {loading ? (
            <Skeleton width={58} height={16} sx={{ bgcolor: "rgba(255,255,255,0.2)" }} />
        ) : (
            <Typography
                sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#fff",
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                {value}
            </Typography>
        )}
    </Box>
);

const Tile = ({ label, value, hint, loading }) => (
    <Box
        sx={{
            minWidth: 0,
            px: { xs: 1.25, md: 1.75 },
            py: { xs: 1, md: 1.4 },
            borderRadius: 2.5,
            bgcolor: C.mist,
            border: `1px solid ${C.line}`,
        }}
    >
        <Typography sx={{ fontSize: 11.5, color: C.inkSoft }} noWrap>
            {label}
        </Typography>
        {loading ? (
            <Skeleton width="70%" height={26} />
        ) : (
            <Typography
                sx={{
                    fontSize: { xs: 15, md: 18 },
                    fontWeight: 800,
                    color: C.ink,
                    lineHeight: 1.25,
                    letterSpacing: "-0.01em",
                    fontVariantNumeric: "tabular-nums",
                }}
                noWrap
            >
                {value}
            </Typography>
        )}
        {hint && !loading && (
            <Typography sx={{ fontSize: 11, color: C.inkSoft }} noWrap>
                {hint}
            </Typography>
        )}
    </Box>
);

const SectionTitle = ({ icon, title, note, right, loading }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 1.25,
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
            <Box
                sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 2,
                    bgcolor: "#E6F4F6",
                    color: C.files,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                    "& svg": { fontSize: 19 },
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: C.ink }}>
                    {title}
                </Typography>
                {loading ? (
                    <Skeleton width={130} height={14} />
                ) : (
                    <Typography sx={{ fontSize: 11.5, color: C.inkSoft }} noWrap>
                        {note}
                    </Typography>
                )}
            </Box>
        </Box>
        {loading ? <Skeleton width={52} height={28} /> : right}
    </Box>
);

/* ------------------------------------------------------------------ */
/* Main dialog                                                         */
/* ------------------------------------------------------------------ */
export default function DatabaseStorageDialog({
    open,
    onClose,
    data,
    loading = false,
    onRefresh,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

    const file = data?.fileStorage ?? {};
    const dbs = data?.databaseStorage ?? {};

    const fileUsed = Number(file.consumedStorageBytes ?? 0);
    const fileCap = Number(file.totalStorageBytes ?? 0);
    const dbTotal = Number(dbs.totalSizeBytes ?? 0);
    const dataBytes = Number(dbs.dataSizeBytes ?? 0);
    const indexBytes = Number(dbs.indexSizeBytes ?? 0);

    const totalUsed = fileUsed + dbTotal;
    const fileFree = Math.max(0, fileCap - fileUsed);

    const filePct = fileCap > 0 ? Math.min(100, (fileUsed / fileCap) * 100) : 0;
    const dataPct = dbTotal > 0 ? (dataBytes / dbTotal) * 100 : 0;
    const indexPct = dbTotal > 0 ? (indexBytes / dbTotal) * 100 : 0;
    const nearFull = filePct >= 90;

    const ringSegments = loading
        ? [
            { key: "f", value: 35, color: "rgba(53,198,214,0.45)" },
            { key: "d", value: 12, color: "rgba(240,162,59,0.45)" },
            { key: "x", value: 53, color: "rgba(255,255,255,0.12)" },
        ]
        : [
            { key: "f", value: fileUsed, color: C.filesBright },
            { key: "d", value: dbTotal, color: C.db },
            { key: "x", value: fileFree, color: "rgba(255,255,255,0.14)" },
        ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobile}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: {
                    m: { sm: 2 },
                    borderRadius: { xs: 0, sm: 5 },
                    overflow: "hidden", // never show a scrollbar
                    height: { xs: "100dvh", sm: "auto" },
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 30px 80px rgba(15,42,61,0.35)",
                },
            }}
        >
            {/* Close / refresh, floating in the top-right corner */}
            {/* CLOSE BUTTON - TOP RIGHT */}
            <Box
                sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    zIndex: 2,
                    color: { xs: "#fff", md: C.ink },
                }}
            >
                <Tooltip title="Close">
                    <IconButton
                        size="small"
                        color="inherit"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* REFRESH BUTTON - LEFT SIDE BELOW TITLE */}
            {onRefresh && (
                <Box
                    sx={{
                        position: "absolute",
                        top: { xs: 65, md: 10 },
                        left: { xs: 12, md: 380 },
                        zIndex: 2,
                    }}
                >
                    <Tooltip title="Refresh">
                        <span>
                            <IconButton
                                size="small"
                                onClick={onRefresh}
                                disabled={loading}
                                sx={{
                                    color: "#0c0b0b",
                                    bgcolor: "rgba(255,255,255,0.12)",
                                    "&:hover": {
                                        bgcolor: "rgba(255,255,255,0.22)",
                                    },
                                }}
                            >
                                <RefreshRoundedIcon
                                    sx={{
                                        fontSize: 20,
                                        animation: loading
                                            ? "spin 1s linear infinite"
                                            : "none",
                                        "@keyframes spin": {
                                            to: { transform: "rotate(360deg)" },
                                        },
                                    }}
                                />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            )}

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "380px 1fr" },
                    gridTemplateRows: { xs: "auto 1fr", md: "auto" },
                }}
            >
                {/* ================= LEFT: hero ================= */}
                <Box
                    sx={{
                        position: "relative",
                        color: "#fff",
                        px: { xs: 2, md: 3.5 },
                        pt: { xs: 2, md: 4 },
                        pb: { xs: 2, md: 4 },
                        background: `linear-gradient(160deg, ${C.ink} 0%, #2964a8 100%)`,
                        display: "flex",
                        flexDirection: "column",
                        gap: { xs: 1.5, md: 3 },
                    }}
                >
                    <Box sx={{ pr: { xs: 9, md: 0 } }}>
                        {loading ? (
                            <>
                                <Skeleton width={150} height={22} sx={{ bgcolor: "rgba(255,255,255,0.25)" }} />
                                <Skeleton width={220} height={14} sx={{ bgcolor: "rgba(255,255,255,0.18)" }} />
                            </>
                        ) : (
                            <>
                                <Typography sx={{ fontSize: { xs: 16, md: 20 }, fontWeight: 800 }}>
                                    Storage overview
                                </Typography>
                                <Typography sx={{ fontSize: 12.5, opacity: 0.75 }} noWrap>
                                    {data?.company?.name || "Company"}
                                </Typography>
                            </>
                        )}



                    </Box>


                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "row", md: "column" },
                            alignItems: "center",
                            gap: { xs: 2, md: 3 },
                        }}
                    >
                        <Ring
                            size={isDesktop ? 200 : 118}
                            stroke={isDesktop ? 18 : 12}
                            segments={ringSegments}
                        >
                            {loading ? (
                                <Skeleton width={70} height={30} sx={{ bgcolor: "rgba(255,255,255,0.25)" }} />
                            ) : (
                                <>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 17, md: 28 },
                                            fontWeight: 800,
                                            lineHeight: 1.1,
                                            letterSpacing: "-0.02em",
                                        }}
                                    >
                                        {fmtBytes(totalUsed)}
                                    </Typography>
                                    <Typography sx={{ fontSize: { xs: 10.5, md: 12 }, opacity: 0.7 }}>
                                        in use
                                    </Typography>
                                </>
                            )}
                        </Ring>

                        <Box sx={{ flex: 1, width: "100%", minWidth: 0 }}>
                            <LegendRow
                                color={C.filesBright}
                                label="Files"
                                value={fmtBytes(fileUsed)}
                                loading={loading}
                            />
                            <LegendRow
                                color={C.db}
                                label="Database"
                                value={fmtBytes(dbTotal)}
                                loading={loading}
                            />
                            <LegendRow
                                color="rgba(255,255,255,0.4)"
                                label="Free file space"
                                value={fmtBytes(fileFree)}
                                loading={loading}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* ================= RIGHT: details ================= */}
                <Box
                    sx={{
                        minHeight: 0,
                        px: { xs: 2, md: 4 },
                        pt: { xs: 2, md: 4 },
                        pb: { xs: 2, md: 4 },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: { xs: "space-between", md: "flex-start" },
                        gap: { xs: 1.5, md: 3.5 },
                        bgcolor: "#fff",
                    }}
                >


                    {/* File storage */}
                    <Box>
                        <SectionTitle
                            loading={loading}
                            icon={<FolderOpenRoundedIcon />}
                            title="File storage"
                            note={`${fmtNum(file.consumedStorageMB)} MB of ${file.totalStorageGB ?? 0} GB used`}
                            right={
                                <Typography
                                    sx={{
                                        fontSize: { xs: 20, md: 26 },
                                        fontWeight: 800,
                                        letterSpacing: "-0.02em",
                                        color: nearFull ? "error.main" : C.ink,
                                    }}
                                >
                                    {filePct.toFixed(1)}%
                                </Typography>
                            }
                        />

                        {loading ? (
                            <LinearProgress
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: C.free,
                                    "& .MuiLinearProgress-bar": { bgcolor: C.files },
                                }}
                            />
                        ) : (
                            <LinearProgress
                                variant="determinate"
                                value={filePct}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: C.free,
                                    "& .MuiLinearProgress-bar": {
                                        borderRadius: 4,
                                        bgcolor: nearFull ? "error.main" : C.files,
                                    },
                                }}
                            />
                        )}

                        <Box
                            sx={{
                                mt: 1.5,
                                display: "grid",
                                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                gap: { xs: 1, md: 1.5 },
                            }}
                        >
                            <Tile label="Files stored" value={fmtNum(file.fileCount)} loading={loading} />
                            <Tile
                                label="Used"
                                value={`${fmtNum(file.consumedStorageMB)} MB`}
                                hint={`${file.consumedStorageGB ?? 0} GB`}
                                loading={loading}
                            />
                            <Tile
                                label="Allocated"
                                value={`${fmtNum(file.totalStorageMB)} MB`}
                                hint={`${file.totalStorageGB ?? 0} GB`}
                                loading={loading}
                            />
                        </Box>
                    </Box>

                    {/* Database storage */}
                    <Box>


                        <SectionTitle
                            loading={loading}
                            icon={<StorageRoundedIcon />}
                            title="Database storage"
                            note={dbs.databaseName || "Database"}
                            right={
                                <Typography
                                    sx={{
                                        fontSize: { xs: 20, md: 26 },
                                        fontWeight: 800,
                                        letterSpacing: "-0.02em",
                                        color: C.ink,
                                    }}
                                >
                                    {dbs.totalSizeMB ?? 0} MB
                                </Typography>
                            }
                        />

                        <Box
                            sx={{
                                display: "flex",
                                height: 8,
                                borderRadius: 4,
                                overflow: "hidden",
                                gap: "2px",
                                bgcolor: C.free,
                            }}
                        >
                            <Box sx={{ width: loading ? "65%" : `${dataPct}%`, bgcolor: C.db, opacity: loading ? 0.5 : 1 }} />
                            <Box sx={{ width: loading ? "25%" : `${indexPct}%`, bgcolor: C.index, opacity: loading ? 0.5 : 1 }} />
                        </Box>

                        {!loading && (
                            <Box sx={{ display: "flex", gap: 2, mt: 0.9 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                                    <Dot color={C.db} size={8} />
                                    <Typography sx={{ fontSize: 11.5, color: C.inkSoft }}>
                                        Table data {dataPct.toFixed(0)}%
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                                    <Dot color={C.index} size={8} />
                                    <Typography sx={{ fontSize: 11.5, color: C.inkSoft }}>
                                        Indexes {indexPct.toFixed(0)}%
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        <Box
                            sx={{
                                mt: 1.5,
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "repeat(2, minmax(0, 1fr))",
                                    md: "repeat(4, minmax(0, 1fr))",
                                },
                                gap: { xs: 1, md: 1.5 },
                            }}
                        >
                            <Tile label="Table data" value={`${dbs.dataSizeMB ?? 0} MB`} loading={loading} />
                            <Tile label="Indexes" value={`${dbs.indexSizeMB ?? 0} MB`} loading={loading} />
                            <Tile label="Tables" value={fmtNum(dbs.tableCount)} loading={loading} />
                            <Tile label="Estimated rows" value={fmtNum(dbs.estimatedRowCount)} loading={loading} />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
}

/* ------------------------------------------------------------------ */
/* Demo                                                                */
/* ------------------------------------------------------------------ */
// export function StorageDialogDemo() {
//     const [open, setOpen] = useState(false);
//     const [loading, setLoading] = useState(false);

//     const handleRefresh = () => {
//         setLoading(true);
//         setTimeout(() => setLoading(false), 2000);
//     };

//     return (
//         <Box
//             sx={{
//                 minHeight: "100vh",
//                 display: "grid",
//                 placeItems: "center",
//                 bgcolor: C.mist,
//                 p: 3,
//             }}
//         >
//             <Box sx={{ textAlign: "center" }}>
//                 <Typography variant="h6" sx={{ mb: 2 }}>
//                     Storage Dialog Demo
//                 </Typography>

//                 <FormControlLabel
//                     control={
//                         <Switch checked={loading} onChange={(e) => setLoading(e.target.checked)} />
//                     }
//                     label="Show skeleton loading"
//                     sx={{ mb: 2 }}
//                 />

//                 <Box>
//                     <Button
//                         variant="contained"
//                         startIcon={<CloudQueueRoundedIcon />}
//                         onClick={() => setOpen(true)}
//                     >
//                         View storage
//                     </Button>
//                 </Box>
//             </Box>

//             <DatabaseStorageDialog
//                 open={open}
//                 onClose={() => setOpen(false)}
//                 data={DUMMY_STORAGE_DATA}
//                 loading={loading}
//                 onRefresh={handleRefresh}
//             />
//         </Box>
//     );
// }